import { Alert, Button, Radio, RadioGroup } from '@odekoteam/doppio';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Box, Flex, Grid, Heading, Paragraph, Text } from 'theme-ui';
import { useClient } from 'urql';

import BannerCoverageNote, {
  joinNames,
} from '@/components/Collection/BannerCoverageNote';
import CategoryBannerDestination, {
  CollectionChoices,
} from '@/components/Collection/CategoryBannerDestination';
import CategoryBannerMarketModal, {
  BANNER_IMAGE_SPECS,
  SHIPPING_CHOICES,
} from '@/components/Collection/CategoryBannerMarketModal';
import {
  BannerDestination,
  BannerImageKey,
  creativeFieldName,
  CreativeTarget,
  defaultMissingDestination,
  defaultMissingShippingCreative,
  defaultTarget,
  DefaultCreative,
  hasArtwork,
  hasPartialArtwork,
  MarketCreative,
  marketsMissingDestination,
  marketsMissingShippingCreative,
  marketStatus,
  marketTarget,
  toDefaultCreative,
  toDefaultInput,
  toMarketCreatives,
  toMarketInputs,
} from '@/components/Collection/categoryBannerMarkets';
import ImageUpload from '@/components/Collection/ImageUpload';
import HiddenMarketsSection from '@/components/Collection/HiddenMarketsSection';
import { LoadingBox } from '@/components/Collection/LoadingBox';
import MarketExceptionSection, {
  sectionStyle,
} from '@/components/Collection/MarketExceptionSection';
import ErrorBanner from '@/components/ErrorBanner';
import {
  CategoryBannerShippingBehavior,
  useDeleteCategoryBannerMutation,
  useGetCategoryBannerForEditQuery,
  useGetCollectionsPageQuery,
  useGetWarehousesQuery,
  useUpsertCategoryBannerMutation,
} from '@/graphql/types/generated';
import {
  FeatureFlag,
  useCategoryBannerCustomDestinations,
  useFeatureFlipper,
} from '@/hooks/featureflipper';
import { useAppDispatch } from '@/hooks/store';
import TwoColLayout from '@/layouts/two-col';
import { ErrorActions } from '@/state/errors';
import { showToast, ToastAlertType } from '@/state/toast';
import { uploadImage } from '@/utils/uploadImage';

// The shared creative's own fields sit flat at the top level, not nested under
// a `defaultCreative` key — `creativeFieldName` names them that way (matching
// `CreateCollectionPromoPage`'s precedent) because it is not a member of
// `warehouses` and has no index to be named by.
type BannerFormData = DefaultCreative & {
  warehouses: MarketCreative[];
};

const SHIPPING_IMAGE_SPECS = [
  { key: 'shippingDesktopImage', label: 'Desktop', width: 2288, height: 480 },
  { key: 'shippingMobileImage', label: 'Mobile', width: 792, height: 320 },
] as const;

const ALL_IMAGE_KEYS: BannerImageKey[] = [
  'desktopImage',
  'mobileImage',
  'shippingDesktopImage',
  'shippingMobileImage',
];

/**
 * Set one field by its path. `FormApi.change` keys its value type off
 * `keyof`, which cannot express `warehouses[2].desktopImage`, so the path
 * form needs the cast rather than each call site inventing its own.
 */
const changeField = (
  form: FormApi<BannerFormData>,
  fieldName: string,
  value: string | null,
) =>
  (form.change as (name: string, value: string | null) => void)(
    fieldName,
    value,
  );

const changeHidden = (
  form: FormApi<BannerFormData>,
  index: number,
  next: boolean,
) =>
  (form.change as (name: string, value: boolean) => void)(
    `warehouses[${index}].hidden`,
    next,
  );

export default function CategoryBannerEditor() {
  const client = useClient();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { categoryId = '' } = useParams<{ categoryId: string }>();
  const isCategoryBannersEnabled = useFeatureFlipper(
    FeatureFlag.CategoryBanners,
  );
  const customDestinationsEnabled = useCategoryBannerCustomDestinations();
  const isOff = !isCategoryBannersEnabled;
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const pendingFilesRef = useRef<Map<string, File>>(new Map());
  const [, setPendingFilesVersion] = useState(0);
  // What the market being edited looked like when its modal opened, so
  // Cancel can restore it. Captured at open time rather than read back off
  // the form, because the modal writes straight into the live form state —
  // there is no separate draft to just discard.
  const editingSnapshotRef = useRef<{
    index: number;
    creative: MarketCreative;
    pendingFiles: Partial<Record<BannerImageKey, File>>;
  } | null>(null);

  const handleFileSelect = useCallback(
    (fieldName: string, file: File | null) => {
      if (file) pendingFilesRef.current.set(fieldName, file);
      else pendingFilesRef.current.delete(fieldName);
      setPendingFilesVersion((version) => version + 1);
    },
    [],
  );

  const [
    {
      data: warehousesData,
      fetching: fetchingWarehouses,
      error: warehousesError,
    },
  ] = useGetWarehousesQuery({ pause: isOff });
  const [{ data: bannerData, fetching: fetchingBanner, error: bannerError }] =
    useGetCategoryBannerForEditQuery({
      pause: isOff || !categoryId,
      variables: { categoryId },
      requestPolicy: 'cache-and-network',
    });

  const [{ data: collectionsData, error: collectionsError }] =
    useGetCollectionsPageQuery({ requestPolicy: 'cache-and-network' });
  const collections: CollectionChoices = useMemo(() => {
    const options = (collectionsData?.collectionsPage ?? [])
      .map((entry) => entry.collection)
      .filter((c): c is { slug: string; name: string } => !!c.slug && !!c.name)
      .map(({ slug, name }) => ({ slug, name }));
    if (collectionsData) return { options, status: 'ready' };
    return { options, status: collectionsError ? 'error' : 'loading' };
  }, [collectionsData, collectionsError]);
  const [, upsertCategoryBanner] = useUpsertCategoryBannerMutation();
  const [, deleteCategoryBanner] = useDeleteCategoryBannerMutation();

  const queryError = bannerError ?? warehousesError;
  useEffect(() => {
    dispatch(ErrorActions.resetErrors());
    if (queryError) dispatch(ErrorActions.setGraphqlErrors(queryError));
  }, [dispatch, queryError]);

  const banner = bannerData?.categoryBannerForEdit;
  const categoryName = banner?.category.name ?? 'this category';
  const warehouses = useMemo(
    () => warehousesData?.warehouses ?? [],
    [warehousesData],
  );

  // Every market gets an entry, configured or not, so a market's position in
  // the form never moves. The field names are built from that position and the
  // pending-files map is keyed by field name, so a shifting index would hand
  // one market's unsaved upload to another. Markets left blank are dropped on
  // submit instead.
  const initialValues: BannerFormData = useMemo(
    () => ({
      ...toDefaultCreative(banner?.defaultCreative),
      warehouses: toMarketCreatives(warehouses, banner?.warehouses ?? []),
    }),
    [banner, warehouses],
  );

  // Subcategories this banner also covers, unless one of them has its own.
  // Nothing else in the form says so, and the question comes up every time.
  const coveredSubcategories = (banner?.category.subcategories ?? []).map(
    (subcategory) => subcategory.name,
  );

  // Which markets the category actually reaches today. Context only — the
  // shared creative applies wherever the category renders regardless of
  // stocking, so nothing here disables a market; it decides what is worth
  // showing by default and what folds away.
  const activeWarehouseIds = useMemo(
    () => new Set((banner?.category.activeWarehouses ?? []).map((w) => w.id)),
    [banner],
  );
  const stocked = warehouses.filter((w) => activeWarehouseIds.has(w.id));
  const unstocked = warehouses.filter((w) => !activeWarehouseIds.has(w.id));

  // A mutation reports trouble two ways — a transport error, or a payload the
  // server filled with field errors. Either one leaves the banner as it was.
  const reportFailure = (
    header: string,
    failures: { message: string }[],
    error?: { message: string },
  ) => {
    dispatch(
      showToast({
        alertType: ToastAlertType.ERROR,
        header,
        content: failures[0]?.message ?? error?.message ?? 'Please try again.',
      }),
    );
  };

  const onSubmit = async (values: BannerFormData) => {
    setSaving(true);
    try {
      const uploaded: Record<string, string> = {};
      try {
        for (const [fieldName, file] of pendingFilesRef.current.entries()) {
          uploaded[fieldName] = await uploadImage(client, file);
        }
      } catch (err) {
        return reportFailure('Banner not saved', [], err as Error);
      }

      const configured = toMarketInputs(values.warehouses, uploaded);
      const defaultCreative = toDefaultInput(values, uploaded);

      const { data, error } = await upsertCategoryBanner({
        input: { categoryId, defaultCreative, warehouses: configured },
      });
      const failures = data?.upsertCategoryBanner?.errors ?? [];
      if (error || failures.length) {
        return reportFailure('Banner not saved', failures, error);
      }
      pendingFilesRef.current.clear();
      navigate('/categories');
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async () => {
    setSaving(true);
    try {
      const { data, error } = await deleteCategoryBanner({
        input: { categoryId },
      });
      const failures = data?.deleteCategoryBanner?.errors ?? [];
      if (error || failures.length) {
        return reportFailure('Banner not removed', failures, error);
      }
      navigate('/categories');
    } finally {
      setSaving(false);
    }
  };

  if (isCategoryBannersEnabled === undefined) {
    return null;
  }
  if (!isCategoryBannersEnabled) {
    return <Navigate to="/" replace />;
  }

  return (
    <TwoColLayout>
      <Box as="header" sx={{ mb: 4, mt: 1 }}>
        <Heading variant="headingMd">{categoryName} banner</Heading>
        <Paragraph
          variant="paragraphLg"
          sx={{ color: 'neutralDark', marginTop: '2px' }}
        >
          One creative for every market, with a different one wherever you need
          it
        </Paragraph>
      </Box>
      <ErrorBanner />
      {coveredSubcategories.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Alert variant="info">
            {`Also shown on ${coveredSubcategories.join(
              ', ',
            )} — unless one of them has a banner of its own.`}
          </Alert>
        </Box>
      )}
      {(fetchingBanner || fetchingWarehouses) && <LoadingBox />}
      {!fetchingBanner && !fetchingWarehouses && !queryError && (
        <Form<BannerFormData>
          onSubmit={onSubmit}
          initialValues={initialValues}
          render={({ form, values, handleSubmit, dirty }) => {
            const defaultHasArtwork = hasArtwork(values);
            // Only one of the two default images set — the state that would
            // otherwise reach Save and quietly delete the stored default,
            // since `toDefaultInput` treats anything short of both as none.
            const defaultPartial = hasPartialArtwork(values);
            const defaultOverridesShipping =
              values.shippingBehavior ===
              CategoryBannerShippingBehavior.Override;

            // The three lists a market can be in. Inheriting is the absence
            // of both, so it is not a list — it is what the coverage line
            // under the shared creative describes.
            const byStatus = warehouses.map((warehouse, index) => ({
              warehouse,
              index,
              stocked: activeWarehouseIds.has(warehouse.id),
              status: marketStatus(values.warehouses[index], defaultHasArtwork),
            }));
            const overriding = byStatus.filter((m) => m.status === 'own');
            const hidden = byStatus.filter((m) => m.status === 'hidden');
            const inheriting = byStatus.filter(
              (m) => m.status === 'inherited' || m.status === 'none',
            );
            const pickable = (list: typeof byStatus) =>
              list.map((m) => ({
                index: m.index,
                name: m.warehouse.name ?? 'Market',
                stocked: m.stocked,
              }));

            const configuredCount =
              (defaultHasArtwork ? 1 : 0) + overriding.length + hidden.length;
            // The default is edited inline rather than through a market row,
            // so it needs its own name in warnings the market list alone
            // would never mention it in.
            const warningNames = (
              defaultMissing: boolean,
              missingMarkets: MarketCreative[],
            ) => [
              ...(defaultMissing ? ['the default creative'] : []),
              ...missingMarkets.map(
                (missing) =>
                  warehouses.find(
                    (warehouse) => warehouse.id === missing.warehouseId,
                  )?.name ?? 'A market',
              ),
            ];
            const missingShippingNames = warningNames(
              defaultMissingShippingCreative(values),
              marketsMissingShippingCreative(values.warehouses),
            );
            const missingDestinationNames = warningNames(
              defaultMissingDestination(values),
              marketsMissingDestination(values.warehouses),
            );
            // A sum rather than `||` keeps the render under the complexity cap.
            const incompleteCount =
              missingShippingNames.length + missingDestinationNames.length;
            const changeDestination = (
              target: CreativeTarget,
              next: BannerDestination,
            ) => {
              const prefix =
                target.kind === 'default' ? '' : `warehouses[${target.index}].`;
              form.batch(() => {
                changeField(
                  form,
                  `${prefix}destinationType`,
                  next.destinationType,
                );
                changeField(
                  form,
                  `${prefix}destinationRef`,
                  next.destinationRef,
                );
              });
            };
            const hasPendingFiles = pendingFilesRef.current.size > 0;
            const unsaved = dirty || hasPendingFiles;

            const clearCreative = (target: CreativeTarget) => {
              changeDestination(target, {
                destinationType: null,
                destinationRef: null,
              });
              ALL_IMAGE_KEYS.forEach((key) => {
                const fieldName = creativeFieldName(target, key);
                pendingFilesRef.current.delete(fieldName);
                changeField(form, fieldName, '');
              });
              changeField(
                form,
                target.kind === 'default'
                  ? 'shippingBehavior'
                  : `warehouses[${target.index}].shippingBehavior`,
                CategoryBannerShippingBehavior.Inherit,
              );
              setPendingFilesVersion((version) => version + 1);
            };

            const discard = () => {
              form.reset();
              pendingFilesRef.current.clear();
              setPendingFilesVersion((version) => version + 1);
            };

            // Opening the modal snapshots the market as it stands, so Cancel has
            // something to restore. Reading `values.warehouses[index]` here
            // rather than from the modal itself is what makes the snapshot
            // predate any edit the modal goes on to make.
            const openMarketModal = (index: number) => {
              const pendingFiles: Partial<Record<BannerImageKey, File>> = {};
              ALL_IMAGE_KEYS.forEach((key) => {
                const file = pendingFilesRef.current.get(
                  creativeFieldName(marketTarget(index), key),
                );
                if (file) pendingFiles[key] = file;
              });
              editingSnapshotRef.current = {
                index,
                creative: values.warehouses[index],
                pendingFiles,
              };
              setEditingIndex(index);
            };

            // Done leaves whatever is in the form as it is — the modal has no
            // separate draft to commit, so closing is all it ever needed to do.
            const closeMarketModal = () => {
              editingSnapshotRef.current = null;
              setEditingIndex(null);
            };

            // Cancel, unlike Done, has to actually undo what the modal did:
            // restore every field the modal can touch to the snapshot taken
            // when it opened, pending files included, then close the same way.
            const cancelMarketModal = () => {
              const snapshot = editingSnapshotRef.current;
              if (snapshot) {
                const target = marketTarget(snapshot.index);
                changeDestination(target, snapshot.creative);
                ALL_IMAGE_KEYS.forEach((key) => {
                  const fieldName = creativeFieldName(target, key);
                  changeField(form, fieldName, snapshot.creative[key]);
                  const file = snapshot.pendingFiles[key];
                  if (file) pendingFilesRef.current.set(fieldName, file);
                  else pendingFilesRef.current.delete(fieldName);
                });
                changeField(
                  form,
                  `warehouses[${snapshot.index}].shippingBehavior`,
                  snapshot.creative.shippingBehavior,
                );
                setPendingFilesVersion((version) => version + 1);
              }
              closeMarketModal();
            };

            const editing =
              editingIndex === null ? null : values.warehouses[editingIndex];
            const editingWarehouse =
              editingIndex === null ? null : warehouses[editingIndex];

            const defaultImage = (spec: {
              key: BannerImageKey;
              label: string;
              width: number;
              height: number;
            }) => {
              const fieldName = creativeFieldName(defaultTarget, spec.key);
              return (
                <ImageUpload
                  key={spec.key}
                  label={spec.label}
                  value={values[spec.key]}
                  disabled={saving}
                  requiredWidth={spec.width}
                  requiredHeight={spec.height}
                  pendingFile={pendingFilesRef.current.get(fieldName) ?? null}
                  onFileSelect={(file) => handleFileSelect(fieldName, file)}
                  onChange={(key) => changeField(form, fieldName, key)}
                />
              );
            };

            return (
              <Flex sx={{ flexDirection: 'column', gap: 5 }}>
                {/* ------------------------------------------------ default */}
                <Box sx={sectionStyle}>
                  <Flex
                    sx={{
                      gap: 3,
                      mb: 3,
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Heading variant="headingSm">Default creative</Heading>
                    {defaultHasArtwork && (
                      <Button
                        variant="ghost"
                        isDisabled={saving}
                        onPress={() => clearCreative(defaultTarget)}
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                  <Grid
                    sx={{ gap: 4, gridTemplateColumns: ['1fr', '2fr 1fr'] }}
                  >
                    {BANNER_IMAGE_SPECS.map(defaultImage)}
                  </Grid>
                  <BannerCoverageNote
                    categoryName={categoryName}
                    hasArtwork={defaultHasArtwork}
                    stocked={stocked.map((w) => w.name ?? 'Market')}
                    unstocked={unstocked.map((w) => w.name ?? 'Market')}
                  />

                  <Box sx={{ mt: 4 }}>
                    <Text variant="paragraphMd" sx={{ fontWeight: 'bold' }}>
                      Shipping customers
                    </Text>
                    <Paragraph
                      variant="paragraphSm"
                      sx={{ color: 'neutralDark', mb: 2 }}
                    >
                      What customers who get their products shipped see, in
                      every market that uses the default
                    </Paragraph>
                    <RadioGroup
                      name="default-shipping-behavior"
                      value={values.shippingBehavior}
                      onChange={(next: string) =>
                        changeField(form, 'shippingBehavior', next)
                      }
                    >
                      {SHIPPING_CHOICES.map((choice) => (
                        <Radio key={choice.value} value={choice.value}>
                          {choice.label}
                        </Radio>
                      ))}
                    </RadioGroup>
                    {defaultOverridesShipping && (
                      <Grid
                        sx={{
                          gap: 4,
                          mt: 3,
                          gridTemplateColumns: ['1fr', '2fr 1fr'],
                        }}
                      >
                        {SHIPPING_IMAGE_SPECS.map(defaultImage)}
                      </Grid>
                    )}
                  </Box>
                  <Box sx={{ mt: 4 }}>
                    <Text variant="paragraphMd" sx={{ fontWeight: 'bold' }}>
                      Link
                    </Text>
                    <Paragraph
                      variant="paragraphSm"
                      sx={{ color: 'neutralDark', mb: 2 }}
                    >
                      Where customers go when they select the banner, in every
                      market that uses the default
                    </Paragraph>
                    <CategoryBannerDestination
                      name="default-destination"
                      destinationType={values.destinationType}
                      destinationRef={values.destinationRef}
                      collections={collections}
                      disabled={saving}
                      customDestinationsEnabled={customDestinationsEnabled}
                      onChange={(next) =>
                        changeDestination(defaultTarget, next)
                      }
                    />
                  </Box>
                </Box>

                <MarketExceptionSection
                  title="Market overrides"
                  emptyText="No market has its own artwork — every market shows the default."
                  disabled={saving}
                  picker={{
                    label: 'Add an override…',
                    markets: pickable(inheriting),
                    onPick: openMarketModal,
                  }}
                  rows={overriding.map((m) => ({
                    id: m.warehouse.id,
                    index: m.index,
                    name: m.warehouse.name ?? 'Market',
                    stocked: m.stocked,
                    thumbnail: {
                      imageKey: values.warehouses[m.index].desktopImage,
                      pendingFile: pendingFilesRef.current.get(
                        creativeFieldName(
                          marketTarget(m.index),
                          'desktopImage',
                        ),
                      ),
                    },
                    actions: [
                      {
                        label: 'Edit',
                        primary: true,
                        onPress: () => openMarketModal(m.index),
                      },
                      {
                        label: 'Revert to default',
                        onPress: () => clearCreative(marketTarget(m.index)),
                      },
                    ],
                  }))}
                />

                <HiddenMarketsSection
                  description={`Nobody in a hidden market sees a banner on ${categoryName} — not this one, and not one from a category above it.`}
                  disabled={saving}
                  markets={byStatus.map((m) => ({
                    index: m.index,
                    name: m.warehouse.name ?? 'Market',
                    stocked: m.stocked,
                    hidden: m.status === 'hidden',
                    wasHidden: Boolean(
                      initialValues.warehouses[m.index]?.hidden,
                    ),
                  }))}
                  onToggle={(index, next) => changeHidden(form, index, next)}
                />

                {editing && editingWarehouse && editingIndex !== null && (
                  <CategoryBannerMarketModal
                    visible
                    disabled={saving}
                    target={marketTarget(editingIndex)}
                    marketName={editingWarehouse.name ?? 'Market'}
                    desktopImage={editing.desktopImage}
                    mobileImage={editing.mobileImage}
                    destinationType={editing.destinationType}
                    destinationRef={editing.destinationRef}
                    collections={collections}
                    customDestinationsEnabled={customDestinationsEnabled}
                    onDestinationChange={(next) =>
                      changeDestination(marketTarget(editingIndex), next)
                    }
                    shippingBehavior={editing.shippingBehavior}
                    shippingDesktopImage={editing.shippingDesktopImage}
                    shippingMobileImage={editing.shippingMobileImage}
                    onShippingBehaviorChange={(next) =>
                      changeField(
                        form,
                        `warehouses[${editingIndex}].shippingBehavior`,
                        next,
                      )
                    }
                    pendingFiles={pendingFilesRef.current}
                    onFileSelect={handleFileSelect}
                    onImageChange={(fieldName, key) =>
                      changeField(form, fieldName, key)
                    }
                    onDone={closeMarketModal}
                    onCancel={cancelMarketModal}
                  />
                )}

                {defaultPartial && (
                  <Alert variant="warning">
                    The default creative needs both a desktop and a mobile
                    image, or neither — remove the remaining one to clear it, or
                    add the other back.
                  </Alert>
                )}

                {missingShippingNames.length > 0 && (
                  <Alert variant="warning">
                    {`${joinNames(
                      missingShippingNames,
                    )} shows different artwork to shipping customers but has none yet.`}
                  </Alert>
                )}

                {missingDestinationNames.length > 0 && (
                  <Alert variant="warning">
                    {`${joinNames(
                      missingDestinationNames,
                    )} links to a collection but none is selected.`}
                  </Alert>
                )}

                {/* --------------------------------------------- save bar */}
                <Flex
                  sx={{
                    gap: 4,
                    py: 4,
                    alignItems: 'center',
                    position: 'sticky',
                    bottom: 0,
                    bg: 'background',
                    borderTop: '1px solid',
                    borderColor: 'neutralLight',
                  }}
                >
                  <Button
                    variant="primary"
                    isDisabled={
                      saving ||
                      !unsaved ||
                      configuredCount === 0 ||
                      defaultPartial ||
                      incompleteCount > 0
                    }
                    onPress={handleSubmit}
                  >
                    Save
                  </Button>
                  {unsaved && (
                    <>
                      <Button
                        variant="ghost"
                        isDisabled={saving}
                        onPress={discard}
                      >
                        Discard
                      </Button>
                      <Text variant="paragraphSm" sx={{ color: 'neutralDark' }}>
                        Unsaved changes
                      </Text>
                    </>
                  )}
                  <Box sx={{ flex: 1 }} />
                  {banner?.categoryBannerId && (
                    <Button
                      variant="secondary"
                      isDisabled={saving}
                      onPress={onRemove}
                    >
                      Remove banner
                    </Button>
                  )}
                </Flex>
              </Flex>
            );
          }}
        />
      )}
    </TwoColLayout>
  );
}
